import React, { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";

// Catches any render error in a page so a single broken view shows a message instead of
// blanking the whole app (no error boundary = React unmounts everything to white).
class ErrorBoundary extends React.Component {
  constructor(p) { super(p); this.state = { error: null, detail: "" }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    // Keep the stack + component stack so a production error is diagnosable (the message alone,
    // e.g. "n is not a function", says nothing once minified). Also logged for DevTools.
    console.error("Route error boundary caught:", error, info?.componentStack);
    // Stale-chunk safety net: a failed dynamic import means a new build was deployed while this
    // tab was open. Reload once to pick up the fresh build instead of showing the error screen.
    if (isChunkError(error) && reloadOnceForStaleChunk()) return;
    this.setState({ detail: `${error?.stack || ""}${info?.componentStack ? "\n\nComponent stack:" + info.componentStack : ""}` });
  }
  componentDidUpdate(prev) { if (prev.routeKey !== this.props.routeKey && this.state.error) this.setState({ error: null, detail: "" }); }
  render() {
    if (this.state.error) {
      return (
        <div style={{ padding: 40, maxWidth: 620 }}>
          <h2 style={{ marginTop: 0 }}>Something went wrong on this page</h2>
          <p className="muted">The rest of the app is fine — try another section, or reload. If it persists, let us know what you were doing.</p>
          <pre style={{ whiteSpace: "pre-wrap", background: "#f6f7f9", padding: 12, borderRadius: 8, fontSize: 12, color: "#b00" }}>
            {String(this.state.error?.message || this.state.error)}
          </pre>
          {this.state.detail && (
            <details style={{ marginBottom: 12 }}>
              <summary className="muted" style={{ cursor: "pointer", fontSize: 12 }}>Technical details</summary>
              <pre style={{ whiteSpace: "pre-wrap", background: "#f6f7f9", padding: 12, borderRadius: 8, fontSize: 11, color: "#555", maxHeight: 260, overflow: "auto" }}>
                {this.state.detail}
              </pre>
            </details>
          )}
          <button className="btn btn-primary" onClick={() => this.setState({ error: null, detail: "" })}>Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}
import { getToken, getStoredUser, setAuth, api } from "./api";
import { applyTheme } from "./theme.js";
import { ToastProvider } from "./components/Toast.jsx";
import { DialogHost } from "./components/Dialog.jsx";
import Sidebar from "./components/Sidebar.jsx";
import Login from "./pages/Login.jsx";
import SetPassword from "./pages/SetPassword.jsx";
import CallIQHub from "./pages/CallIQHub.jsx";
import ForecastReminder from "./components/ForecastReminder.jsx";
import ProbationBanner from "./components/ProbationBanner.jsx";
import TwoStepBanner from "./components/TwoStepBanner.jsx";
import OnboardingBanner from "./components/OnboardingBanner.jsx";
import { RcWidgetLoader } from "./components/phone.jsx";
import ExportStatus from "./components/ExportStatus.jsx";
import { track } from "./telemetry";   // usage telemetry (review 2026-09-21, managers)

// Every page is code-split so a user only downloads the screen they open (and heavy deps like
// recharts/d3 load with the pages that use them, not on first paint). Login / SetPassword stay
// eager — they're the entry point — and CallIQHub is a tiny shared wrapper.

// A failed dynamic import ("Failed to fetch dynamically imported module") almost always means a
// NEW build was deployed while this tab was open — the old chunk hashes it knows about are gone
// from the server. Reloading fetches the fresh index.html (nginx serves it no-cache) and heals
// the tab. Time-guarded so a genuinely broken asset / offline network can't cause a reload loop.
export function reloadOnceForStaleChunk() {
  const last = Number(sessionStorage.getItem("calliq_chunk_reload") || 0);
  if (Date.now() - last < 30_000) return false;
  sessionStorage.setItem("calliq_chunk_reload", String(Date.now()));
  window.location.reload();
  return true;
}
const isChunkError = (err) =>
  /dynamically imported module|Importing a module script failed|Failed to fetch dynamically/i
    .test(String(err?.message || err));
const lazyRetry = (importer) => lazy(() =>
  importer().catch((err) => {
    if (isChunkError(err) && reloadOnceForStaleChunk()) {
      return new Promise(() => {});   // the page is reloading — never resolve
    }
    throw err;
  }));

const Account = lazyRetry(() => import("./pages/Account.jsx"));
const People = lazyRetry(() => import("./pages/People.jsx"));
// 👥 People & Admin — the combined managers' dashboard (Salem 2026-08-20). A thin shell
// that mounts People and the Admin Hub as tabs; both pages are unchanged.
const PeopleHub = lazyRetry(() => import("./pages/PeopleHub.jsx"));
const HRProfile = lazyRetry(() => import("./pages/HRProfile.jsx"));
const HolidayCalendar = lazyRetry(() => import("./pages/HolidayCalendar.jsx"));
const Library = lazyRetry(() => import("./pages/Library.jsx"));
const CompanyIQ = lazyRetry(() => import("./pages/CompanyIQ.jsx"));
const CompanyDetail = lazyRetry(() => import("./pages/CompanyDetail.jsx"));
const SalesIQ = lazyRetry(() => import("./pages/SalesIQ.jsx"));
const MorningDashboard = lazyRetry(() => import("./pages/MorningDashboard.jsx"));
const CommandCentre = lazyRetry(() => import("./pages/CommandCentre.jsx"));
// Canvases (Salem 2026-09-27): a manager's pinned Oracle charts, side by side, re-drawn live.
const CanvasPage = lazyRetry(() => import("./pages/Canvas.jsx"));
const CanvasIndex = lazyRetry(() => import("./pages/Canvas.jsx").then((m) => ({ default: m.CanvasIndex })));
const CallDetail = lazyRetry(() => import("./pages/CallDetail.jsx"));
const Missed = lazyRetry(() => import("./pages/Missed.jsx"));
const Playlists = lazyRetry(() => import("./pages/Playlists.jsx"));
const Insights = lazyRetry(() => import("./pages/Insights.jsx"));
const Reports = lazyRetry(() => import("./pages/Reports.jsx"));
const Settings = lazyRetry(() => import("./pages/Settings.jsx"));
const OrderEntry = lazyRetry(() => import("./pages/OrderEntry.jsx"));
const SalesPerformance = lazyRetry(() => import("./pages/SalesPerformance.jsx"));
const LeagueTable = lazyRetry(() => import("./pages/LeagueTable.jsx"));
const IntelligenceHealth = lazyRetry(() => import("./pages/IntelligenceHealth.jsx"));
const Scorecard = lazyRetry(() => import("./pages/Scorecard.jsx"));
const OG = lazyRetry(() => import("./pages/OG.jsx"));
const LeadHub = lazyRetry(() => import("./pages/LeadHub.jsx"));
const MyLeadHub = lazyRetry(() => import("./pages/MyLeadHub.jsx"));
const Board = lazyRetry(() => import("./pages/Board.jsx"));
const TrainIQ = lazyRetry(() => import("./pages/TrainIQ.jsx"));
const ToolkitLibrary = lazyRetry(() => import("./pages/ToolkitLibrary.jsx"));
// RepIQ Conversations — WhatsApp for Business (design v2). Everyone signed in gets the
// inbox: reps see their own threads + the shared queue, Operations and managers see all
// (scoped server-side); the manager tabs (Campaigns/Studio/Control Room) gate on /status.
const WhatsApp = lazyRetry(() => import("./pages/WhatsApp.jsx"));
// Ask Order Toolkit — the floating 🧰 dock on every protected page (lazy so the toolkit
// code isn't in the first paint; it mounts closed and costs nothing until opened).
const ToolkitDock = lazyRetry(() => import("./components/ToolkitDock.jsx"));
// Schedule 5 / Cobra live OUT here, not in the Order Hub page, so a reconciliation survives
// walking off to look at an order and coming back (Salem 2026-08-27).
const ReconDock = lazyRetry(() => import("./components/ReconDock.jsx"));
const LiveAssist = lazyRetry(() => import("./components/LiveAssist.jsx"));
// The sales-floor TV display: public route (screens pair with a code, not a login).
const TvApp = lazyRetry(() => import("./tv/TvApp.jsx"));
// A shared call review — the public side of an Export link (token-gated, no login).
const SharedCall = lazyRetry(() => import("./pages/SharedCall.jsx"));
// September 2026 review pages (review 2026-09-21: switchoff, coverage, reachability, sources, retention, managers, renewals)
const SwitchOff = lazyRetry(() => import("./pages/SwitchOff.jsx"));
const Coverage = lazyRetry(() => import("./pages/Coverage.jsx"));
const Reachability = lazyRetry(() => import("./pages/Reachability.jsx"));
const FibreComing = lazyRetry(() => import("./pages/FibreComing.jsx"));
const Outages = lazyRetry(() => import("./pages/Outages.jsx"));      // Outage radar (Salem 2026-10-01)
const SignalSources = lazyRetry(() => import("./pages/SignalSources.jsx"));
// Buying signals & Landscaping, promoted from Directory-toolbar modals to their own Prospecting
// sub-tabs (Salem, 2026-09-26) — manager/admin gated, same as the buttons they replaced.
const BuyingSignals = lazyRetry(() => import("./pages/BuyingSignals.jsx"));
const Landscaping = lazyRetry(() => import("./pages/Landscaping.jsx"));
const MyAccounts = lazyRetry(() => import("./pages/MyAccounts.jsx"));
const Trackers = lazyRetry(() => import("./pages/Trackers.jsx"));
const Commit = lazyRetry(() => import("./pages/Commit.jsx"));
const Renewals = lazyRetry(() => import("./pages/Renewals.jsx"));
// The Games ("Theirs", 2026-10-07): the rep's own view of OG — reps and BCs only (RepArea).
const Games = lazyRetry(() => import("./pages/Games.jsx"));

function ProtectedLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  // Usage telemetry (review 2026-09-21, managers — §7.1): one "view" per route change, batched by telemetry.js.
  useEffect(() => { track(location.pathname, "view"); }, [location.pathname]);
  // "In their pocket" (Oct 2026): a tapped phone notification opens the app with ?m=<message id>.
  // Record the open (the service worker has no auth header, so it is this call that counts) and
  // drop the param so a refresh or a share never re-posts it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const m = params.get("m");
    if (!m) return;
    api.post(`/api/rep/messages/${encodeURIComponent(m)}/opened`).catch(() => { /* best effort */ });
    params.delete("m");
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "", hash: location.hash }, { replace: true });
  }, [location.search, location.pathname, location.hash, navigate]);
  const token = getToken();
  const [user, setUser] = useState(getStoredUser());
  // Refresh the user from the server so the derived sales_role (used for role-based routing)
  // is always current, even for sessions that logged in before sales_role existed.
  useEffect(() => {
    if (!token) return;
    api.get("/api/auth/me").then((u) => { setUser(u); setAuth(token, u); applyTheme(u.theme || "classic"); }).catch(() => {});
  }, [token]);
  if (!token) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  return (
    // The shell (design system 1.0, phase 3): a sidebar column and a content column on one
    // grid. The sidebar measures ITSELF (layout.css): full at ≥1200px, an icon rail at
    // 900–1199px, a top bar with a menu button below 900px — all from the shell's width,
    // never the window's, so the pages lay out the same beside a rail or a full sidebar.
    <div className="rq-shell"><div className="app-shell rq-app">
      <Sidebar user={user} />
      <main className="app-content rq-main">
        {/* The app-wide banners sit flush with the top of the window, outside the page
            padding (Salem 2026-10-09). The wrapper collapses to nothing when none shows. */}
        <div className="rq-banners">
          {/* Two-step verification owed by this person's role (2026-10-01). */}
          <TwoStepBanner user={user} />
          {/* Managers only, and only when a probation review is due this week or overdue. */}
          <ProbationBanner />
          {/* Admins only — a new starter's checks need a look, or one is held. */}
          <OnboardingBanner />
        </div>
        <ErrorBoundary routeKey={location.pathname}>
          <Suspense fallback={<div className="page"><div className="busy-bar" role="progressbar" aria-label="Opening the page"><div /></div><div className="muted small">Opening…</div></div>}>
            <Outlet context={{ user }} />
          </Suspense>
        </ErrorBoundary>
      </main>
      <ForecastReminder />
      {/* Ask Order Toolkit — the floating 🧰 on every page (never on /tv or /login:
          they live outside this layout). */}
      <Suspense fallback={null}>
        <ToolkitDock />
      </Suspense>
      {/* The Schedule 5 / Cobra reconciliation windows — opened from the Order Hub, but held here
          so navigating away can't take the uploaded file, the preview or the ticks with it. */}
      <Suspense fallback={null}>
        <ReconDock />
      </Suspense>
      {/* ⚡ Live Assist — the on-call panel: who you're talking to, the card for what they just
          said, a nudge or two. Opens itself when a call connects (Salem 2026-09-05). */}
      <Suspense fallback={null}>
        <LiveAssist />
      </Suspense>
      {/* In-app RingCentral dialer — loads only when RINGCENTRAL_EMBED_CLIENT_ID is set. */}
      <RcWidgetLoader />
      {/* CSV export progress for every page (Salem 2026-10-08). */}
      <ExportStatus />
    </div></div>
  );
}

function AdminOnly({ children }) {
  const user = getStoredUser();
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

const isManager = (u) => u?.sales_role === "manager";
const isOps = (u) => u?.platform_role === "operations";

// People management is for managers and admins; everyone else is sent home.
function PeopleArea({ children }) {
  const { user } = useOutletContext() || {};
  if (user && user.role !== "admin" && !isManager(user)) return <Navigate to="/" replace />;
  return children;
}

// "/" lands each role on the right home: Operations → Order Entry (they aren't sales reps),
// managers → Command Centre, everyone else → Today.
function RoleLanding() {
  const { user } = useOutletContext() || {};
  if (isOps(user)) return <Navigate to="/orders" replace />;
  return <Navigate to={isManager(user) ? "/command-centre" : "/today"} replace />;
}

// Managers never see the rep morning dashboard (brief: they get the Command Centre, full stop).
// Operations aren't treated as sales reps — Today is hidden for them; they land on Order Entry.
function RepArea({ children }) {
  const { user } = useOutletContext() || {};
  if (isOps(user)) return <Navigate to="/orders" replace />;
  if (isManager(user)) return <Navigate to="/command-centre" replace />;
  return children;
}

// The Command Centre is for managers/admin only; reps are sent back to their dashboard.
function ManagerArea({ children }) {
  const { user } = useOutletContext() || {};
  if (user && !isManager(user)) return <Navigate to="/today" replace />;
  return children;
}

// The Toolkit Library is CURATED, not managed: Operations decide which Sales Toolkit
// documents get uploaded and refreshed (Salem 2026-08-20), so they reach it alongside
// managers and admin. The coaching tabs INSIDE the page stay manager-only — the page
// hides them from the `manager` / `curator` flags on /toolkit/status.
function LibraryArea({ children }) {
  const { user } = useOutletContext() || {};
  if (user && user.role !== "admin" && !isManager(user) && !isOps(user)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

// ---- Review 7.4 (Sep 2026): "Some screens are reachable by URL without a role check —
// /orders, /league, /sales-performance, /reports, /myleads, /people/:id." The backend already
// scopes what each role can change, so these guards are tidiness: each mirrors the sidebar
// exactly, so a typed URL never shows more than the menu does. Every miss is a SILENT
// redirect to "/" (RoleLanding then picks the right home for the role) — no error, no toast.
const isAdmin = (u) => u?.role === "admin";
const isManagerOrAdmin = (u) => isAdmin(u) || isManager(u);

// Order Hub: Operations (it is their home), managers and admin — never reps or BCs.
function OrdersArea({ children }) {
  const { user } = useOutletContext() || {};
  if (user && !isOps(user) && !isManagerOrAdmin(user)) return <Navigate to="/" replace />;
  return children;
}

// Sales views — League Table, Sales Performance, AI Reports. Every sales role can open them:
// SalesIQ links reps and BCs to the team-wide views (Salem 2026-07-23) and the sidebar shows
// AI Reports to everyone but Operations, so Operations is the only role sent home.
function SalesArea({ children }) {
  const { user } = useOutletContext() || {};
  if (user && isOps(user)) return <Navigate to="/" replace />;
  return children;
}

// My Leads is the rep's / BC's own Lead Hub. Managers and admin have the team Lead Hub
// instead, so they go there; Operations have no leads view at all.
function MyLeadsArea({ children }) {
  const { user } = useOutletContext() || {};
  if (user && isManagerOrAdmin(user)) return <Navigate to="/leads" replace />;
  if (user && isOps(user)) return <Navigate to="/" replace />;
  return children;
}

// A People profile: managers/admin (PeopleArea) or the person themselves — the sidebar's
// "My profile" link. Anyone else typing /people/<id> goes home.
function ProfileArea({ children }) {
  const { user } = useOutletContext() || {};
  const { id } = useParams();
  if (user && !isManagerOrAdmin(user) && Number(user.id) !== Number(id)) return <Navigate to="/" replace />;
  return children;
}

// Intel Health: managers and admin (the sidebar entry is for non-admin managers; admins reach
// it from Settings → Intel Health, so the route stays open to them too).
function IntelHealthArea({ children }) {
  const { user } = useOutletContext() || {};
  if (user && !isManagerOrAdmin(user)) return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <ToastProvider>
      <DialogHost />
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/set-password/:token" element={<SetPassword />} />
          {/* OG — Oppsession Games TV display (full-screen, pairs with a code). */}
          <Route path="/tv" element={
            <Suspense fallback={<div style={{ position: "fixed", inset: 0, background: "#0D0D1A" }} />}>
              <TvApp />
            </Suspense>
          } />
          {/* Public call review from an Export link — outside the protected layout. */}
          <Route path="/shared/:token" element={
            <Suspense fallback={<div style={{ padding: 60 }} />}>
              <SharedCall />
            </Suspense>
          } />
          <Route element={<ProtectedLayout />}>
            <Route path="/" element={<RoleLanding />} />
            <Route path="/account" element={<Account />} />
            <Route path="/people" element={<PeopleArea><PeopleHub /></PeopleArea>} />
            <Route path="/people/:id" element={<ProfileArea><HRProfile /></ProfileArea>} />
            <Route path="/today" element={<RepArea><MorningDashboard /></RepArea>} />
            <Route path="/command-centre" element={<ManagerArea><CommandCentre /></ManagerArea>} />
            <Route path="/canvas" element={<ManagerArea><CanvasIndex /></ManagerArea>} />
            <Route path="/canvas/:id" element={<ManagerArea><CanvasPage /></ManagerArea>} />
            <Route path="/og" element={<ManagerArea><OG /></ManagerArea>} />
            <Route path="/games" element={<RepArea><Games /></RepArea>} />
            <Route path="/adminhub" element={<HolidayCalendar />} />
            <Route path="/companyiq" element={<CompanyIQ />} />
            <Route path="/companyiq/company/:id" element={<CompanyDetail />} />
            {/* Review 2026-09-21 — new businesses (reachability; buttons manager-gated in page + server),
                Switch-off 2027 and Mobile coverage (any signed-in user), My accounts (retention;
                Operations read-only in the page and the API). Signal Sources moved out of CompanyIQ
                and into Settings (Salem, 2026-09-25) — admin-only now, same as Toolkit Library's/
                Intel Health's Settings tab, but with NO standalone route (Salem's explicit choice:
                non-admin managers lose access entirely rather than keeping a nav fallback). */}
            <Route path="/companyiq/new-businesses" element={<Reachability />} />
            <Route path="/switchoff" element={<SwitchOff />} />
            <Route path="/coverage" element={<Coverage />} />
            {/* Fibre coming (Salem, 2026-09-26): the fourth CompanyIQ signal tab, same family
                as Switch-off/Mobile coverage — open to everyone, same as those two. */}
            <Route path="/companyiq/fibre" element={<FibreComing />} />
            <Route path="/companyiq/outages" element={<Outages />} />
            {/* Signal Sources moved back OUT of Settings and into CompanyIQ as an admin-only
                tab (Salem, 2026-09-26) — same admin-only gate as before (AdminOnly, matching
                the backend's require_admin), just reached from CompanyIQ's tab strip now
                instead of a Settings section. */}
            <Route path="/companyiq/sources" element={<AdminOnly><SignalSources /></AdminOnly>} />
            <Route path="/companyiq/signals" element={<ManagerArea><BuyingSignals /></ManagerArea>} />
            <Route path="/companyiq/landscaping" element={<ManagerArea><Landscaping /></ManagerArea>} />
            <Route path="/accounts" element={<MyAccounts />} />
            <Route path="/companyiq/directory" element={<Navigate to="/companyiq" replace />} />
            <Route path="/salesiq" element={<SalesIQ />} />
            <Route path="/orders" element={<OrdersArea><OrderEntry /></OrdersArea>} />
            <Route path="/leads" element={<ManagerArea><LeadHub /></ManagerArea>} />
            <Route path="/myleads" element={<MyLeadsArea><MyLeadHub /></MyLeadsArea>} />
            {/* Renewals pipeline (review 2026-09-21, renewals — §6.2): managers get the board by pillar,
                reps their own; Operations home (SalesArea is exactly that gate). */}
            <Route path="/renewals" element={<SalesArea><Renewals /></SalesArea>} />
            {/* Managers' builds (review 2026-09-21, managers): the commit view. Trackers moved
                out of here and into CallIQ as a tab, between Recordings and Missed & voicemail
                (Salem, 2026-09-25) — still manager/admin only via ManagerArea, just reached
                through CallIQ now rather than its own sidebar row. */}
            <Route path="/trackers" element={<ManagerArea><CallIQHub><Trackers /></CallIQHub></ManagerArea>} />
            <Route path="/commit" element={<ManagerArea><Commit /></ManagerArea>} />
            <Route path="/sales-performance" element={<SalesArea><SalesPerformance /></SalesArea>} />
            <Route path="/league" element={<SalesArea><div className="page"><LeagueTable /></div></SalesArea>} />
            <Route path="/library" element={<CallIQHub><Library /></CallIQHub>} />
            <Route path="/recordings" element={<CallIQHub><Library /></CallIQHub>} />
            <Route path="/calls/:id" element={<CallDetail />} />
            {/* While You Were Away — a CallIQ tab, between Recordings and Insights
                (Salem 2026-09-08). No role gate: a rep sees their own DDI, a cover sees what
                is routed to them, a manager sees the LB — all enforced server-side. */}
            <Route path="/missed" element={<CallIQHub><Missed /></CallIQHub>} />
            <Route path="/playlists" element={<CallIQHub><Playlists /></CallIQHub>} />
            <Route path="/insights" element={<CallIQHub><Insights /></CallIQHub>} />
            <Route path="/insights/:tab" element={<CallIQHub><Insights /></CallIQHub>} />
            {/* The Board — the pinboard. Everyone can see it; managers arrange it. */}
            <Route path="/board" element={<Board />} />
            <Route path="/trainiq" element={<TrainIQ />} />
            <Route path="/toolkit" element={<LibraryArea><ToolkitLibrary /></LibraryArea>} />
            <Route path="/whatsapp" element={<WhatsApp />} />
            <Route path="/reports" element={<SalesArea><Reports /></SalesArea>} />
            <Route path="/intelligence-health" element={<IntelHealthArea><IntelligenceHealth /></IntelHealthArea>} />
            <Route path="/scorecard" element={<IntelHealthArea><Scorecard /></IntelHealthArea>} />
            <Route
              path="/settings"
              element={
                <AdminOnly>
                  <Settings />
                </AdminOnly>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ToastProvider>
  );
}
